import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { formatApiErrorDetail } from "@/lib/api";
import { ArrowRight, Utensils, Eye, EyeOff } from "lucide-react";
import Logo from "@/components/Logo";

const LOGIN_BG =
  "https://images.unsplash.com/photo-1776993298456-98c71c0e177e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2MzR8MHwxfHNlYXJjaHw0fHxmaW5lJTIwZGluaW5nJTIwcmVzdGF1cmFudCUyMGludGVyaW9yJTIwbHV4dXJ5fGVufDB8fHx8MTc4NTQwMzkxMnww&ixlib=rb-4.1.0&q=85";

export default function LoginPage() {
  const nav = useNavigate();
  const { login } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await login(email, password);
      toast.success("Welcome back");
      nav("/dashboard");
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Login failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-background" data-testid="login-page">
      {/* Left visual */}
      <div className="relative hidden lg:block overflow-hidden">
        <img
          src={LOGIN_BG}
          alt="Royal Wow Meals dining room"
          className="absolute inset-0 w-full h-full object-cover"
          aria-hidden="true"
        />
        <div className="absolute inset-0 bg-black/55" aria-hidden="true" />
        <div className="relative z-10 h-full flex flex-col justify-between p-14 text-white">
          <div className="flex items-center gap-3 fade-up">
            <Logo variant="mark" className="w-9 h-9" />
            <span className="tracking-[0.35em] text-xs">ROYAL WOW MEALS · EST. 2026</span>
          </div>
          <div className="fade-up" style={{ animationDelay: "120ms" }}>
            <h1 className="font-serif text-6xl leading-[1.05] tracking-tight">
              Royal Wow
              <br />
              <span className="italic text-[#D4AF37]">Meals</span>
            </h1>
            <p className="mt-6 max-w-md text-white/80 font-light">
              The private ledger of our kitchen. Attendance, hours, and payroll — kept with grace.
            </p>
          </div>
          <div className="fade-up text-xs tracking-widest text-white/60" style={{ animationDelay: "220ms" }}>
            MANAGER PORTAL · v1
          </div>
        </div>
      </div>

      {/* Right form */}
      <div className="flex flex-col justify-center px-8 sm:px-14 lg:px-24 py-16">
        <div className="max-w-md fade-up">
          <div className="lg:hidden mb-10 flex items-center gap-3">
            <Logo variant="mark" className="w-12 h-12" />
            <h1 className="font-serif text-3xl tracking-tight">Royal Wow Meals</h1>
          </div>
          <p className="text-xs tracking-[0.3em] text-muted-foreground uppercase">Sign in</p>
          <h2 className="mt-3 font-serif text-4xl tracking-tight">Manager access</h2>
          <p className="mt-3 text-muted-foreground">
            Attendance intelligence for Royal Wow Meals.
          </p>

          <form onSubmit={submit} className="mt-10 space-y-6">
            <div>
              <Label htmlFor="email" className="text-xs uppercase tracking-widest text-muted-foreground">
                Email
              </Label>
              <Input
                id="email"
                data-testid="login-email-input"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="mt-2 h-12 rounded-none border-0 border-b-2 bg-transparent px-0 focus-visible:ring-0 focus-visible:border-primary text-base"
                placeholder="you@royalwowmeals.com"
              />
            </div>
            <div>
              <Label htmlFor="password" className="text-xs uppercase tracking-widest text-muted-foreground">
                Password
              </Label>
              <div className="relative">
                <Input
                  id="password"
                  data-testid="login-password-input"
                  type={showPw ? "text" : "password"}
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="mt-2 h-12 rounded-none border-0 border-b-2 bg-transparent px-0 pr-10 focus-visible:ring-0 focus-visible:border-primary text-base"
                  placeholder="••••••••"
                />
                <button
                  type="button"
                  onClick={() => setShowPw((v) => !v)}
                  data-testid="toggle-password-btn"
                  aria-label={showPw ? "Hide password" : "Show password"}
                  className="absolute right-0 bottom-3 text-muted-foreground hover:text-foreground p-1"
                >
                  {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>
            <Button
              type="submit"
              disabled={busy}
              data-testid="login-submit-btn"
              className="w-full h-12 rounded-full bg-primary text-primary-foreground hover:bg-primary/90 transition-colors"
            >
              {busy ? "Signing in…" : (
                <span className="flex items-center gap-2">
                  Enter dashboard <ArrowRight className="w-4 h-4" aria-hidden="true" />
                </span>
              )}
            </Button>
          </form>

          <div className="mt-14 pt-6 border-t border-border text-sm text-muted-foreground flex items-center justify-between">
            <Link
              to="/forgot-password"
              data-testid="forgot-password-link"
              className="hover:text-foreground transition-colors"
            >
              Forgot password?
            </Link>
          </div>

          <div className="mt-6 text-sm text-muted-foreground flex items-center justify-between">
            <span>Employee?</span>
            <Link
              to="/kiosk"
              data-testid="go-to-kiosk-link"
              className="text-primary font-medium hover:underline underline-offset-4"
            >
              Open PIN kiosk →
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
